import { seededRandomPrice } from './pricing';
import {
  getCarCatalog,
  getCruiseCatalog,
  getHotelCatalog,
  getPackageCatalog,
} from '../data/dummyCatalog';

function norm(s = '') {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function matchText(haystack, needle) {
  if (!needle) return true;
  const n = norm(needle);
  return norm(haystack).includes(n);
}

function scoreMatch(item, fields, query) {
  if (!query) return 1;
  const n = norm(query);
  let score = 0;
  fields.forEach((f) => {
    const v = norm(String(item[f] || ''));
    if (v === n) score += 10;
    else if (v.includes(n)) score += 5;
    else if (n.includes(v) && v.length > 3) score += 2;
  });
  return score;
}

/**
 * The demo catalog only covers ~50 cities. For any other city, reuse catalog
 * records but place them in the searched city so results stay relevant.
 */
const RELOCATE_COUNT = 24;
const CAR_SPOTS = ['Airport', 'Downtown', 'Station'];

function relocate(items, city, mapItem) {
  const key = norm(city);
  return items.slice(0, RELOCATE_COUNT).map((item, i) => ({ ...mapItem(item, i), id: `${item.id}-${key}` }));
}

const swapCity = (text, from, to) => String(text || '').split(from).join(to);

function searchCatalog(items, params, fields, limit = 80) {
  const q = params.destination || params.pickup || params.port || params.keyword || '';
  const scored = items
    .map((item) => ({ item, score: scoreMatch(item, fields, q) }))
    .filter(({ score }) => score > 0 || !q);

  if (!scored.length) {
    // Unknown place: let the caller relocate demo records to it.
    return q ? [] : items.slice(0, limit);
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map(({ item }) => item);
}

export function searchHotelsFromCatalog(params, limit = 80) {
  const items = getHotelCatalog();
  let results = searchCatalog(items, params, ['destination', 'citySlug', 'name'], limit);

  if (results.length < 15 && params.destination) {
    results = items.filter((h) => matchText(h.destination, params.destination)).slice(0, limit);
  }
  if (results.length < 10 && params.destination) {
    const city = params.destination.trim();
    results = relocate(items, city, (h) => ({
      ...h,
      name: swapCity(h.name, h.destination, city),
      address: swapCity(h.address, h.destination, city),
      destination: city,
    }));
  }
  if (results.length < 10) {
    results = items.slice(0, limit);
  }

  const guests = parseInt(params.guests, 10) || 2;
  const rooms = parseInt(params.rooms, 10) || 1;

  return results.map((h, idx) => {
    const perNight = seededRandomPrice(`${h.id}-${guests}-${rooms}-${idx}`);
    const price = perNight * rooms;
    return {
      ...h,
      price,
      perNight,
      guests,
      rooms,
    };
  });
}

export function searchCarsFromCatalog(params, limit = 80) {
  const items = getCarCatalog();
  const pickup = params.pickup || '';
  let results = searchCatalog(items, { pickup }, ['pickup', 'pickupSlug'], limit);

  if (pickup && results.length < 15) {
    results = items.filter((c) => matchText(c.pickup, pickup)).slice(0, limit);
  }
  if (results.length < 10 && pickup) {
    const city = pickup.trim();
    results = relocate(items, city, (c, i) => ({ ...c, pickup: `${city} ${CAR_SPOTS[i % CAR_SPOTS.length]}` }));
  }
  if (results.length < 10) {
    results = items.slice(0, limit);
  }

  if (params.carType && params.carType !== 'any') {
    const typed = results.filter((c) => c.type === params.carType);
    if (typed.length) results = typed;
  }

  const pickupDate = params.pickupDate;
  const dropoffDate = params.dropoffDate;
  let days = 3;
  if (pickupDate && dropoffDate) {
    days = Math.max(1, Math.round((new Date(dropoffDate) - new Date(pickupDate)) / 86400000));
  }

  return results.map((c, idx) => {
    const perDay = seededRandomPrice(`${c.id}-${days}-${idx}`);
    const price = perDay * days;
    return {
      ...c,
      days,
      price,
      perDay,
    };
  });
}

export function searchPackagesFromCatalog(params, limit = 80) {
  const items = getPackageCatalog();
  let results = searchCatalog(items, params, ['destination', 'citySlug', 'title'], limit);
  if (results.length < 10 && params.destination) {
    const city = params.destination.trim();
    results = relocate(items, city, (p) => ({
      ...p,
      title: swapCity(p.title, p.destination, city),
      destination: city,
    }));
  }
  if (results.length < 10) results = items.slice(0, limit);

  const travelers = parseInt(params.travelers, 10) || 2;
  return results.map((p, idx) => {
    const perPerson = seededRandomPrice(`${p.id}-${travelers}-${idx}`);
    const price = perPerson * travelers;
    return {
      ...p,
      price,
      perPerson,
      travelers,
    };
  });
}

export function searchCruisesFromCatalog(params, limit = 80) {
  const items = getCruiseCatalog();
  const port = params.port || '';
  const dest = params.destination || '';

  let results = items.filter((c) => {
    const portOk = !port || matchText(c.port, port) || matchText(c.portSlug, port);
    const destOk = !dest || matchText(c.destination, dest) || matchText(c.regionSlug, dest);
    return portOk && destOk;
  });

  if (results.length < 10 && (port || dest)) {
    const byPort = port ? items.filter((c) => matchText(c.port, port)) : [];
    const byRegion = dest ? items.filter((c) => matchText(c.destination, dest)) : [];
    const base = byPort.length ? byPort : byRegion.length ? byRegion : items;
    results = relocate(base, port || dest, (c) => ({
      ...c,
      port: port ? port.trim() : c.port,
      destination: dest ? dest.trim() : c.destination,
    }));
  }
  if (results.length < 10) {
    results = searchCatalog(items, { port }, ['port', 'portSlug', 'destination'], limit);
  }
  if (results.length < 10) results = items.slice(0, limit);

  return results.slice(0, limit);
}
